/**
 * AdFormModal Component
 * Modal wrapper for AdForm component used in desktop view
 */

import { useState, useEffect } from 'react';
import Modal from '../common/Modal/Modal';
import { AdForm, type AdFormData } from './AdForm';
import { createAd, updateAd, getMyAds } from '../../services/membersApi';
import type { Ad } from '../../types';
import './AdFormModal.css';

interface AdFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  adId?: string | null;
  onSuccess?: (adId: string) => void;
}

const AdFormModal = ({ isOpen, onClose, adId, onSuccess }: AdFormModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAd, setIsLoadingAd] = useState(false);
  const [error, setError] = useState('');
  const [initialData, setInitialData] = useState<Ad | undefined>();
  const isEditing = !!adId;

  // Load existing ad data if editing
  useEffect(() => {
    if (!isOpen) {
      setInitialData(undefined);
      setError('');
      return;
    }

    const loadAd = async () => {
      if (!adId) return;

      setIsLoadingAd(true);
      setError('');

      try {
        const adsResult = await getMyAds();
        const ad = adsResult.data.find((a) => a.id === adId);

        if (ad) {
          setInitialData(ad);
        } else {
          setError('找不到該廣告');
        }
      } catch (err) {
        console.error('Failed to load ad:', err);
        setError('載入廣告失敗');
      } finally {
        setIsLoadingAd(false);
      }
    };

    loadAd();
  }, [isOpen, adId]);

  const handleSubmit = async (data: AdFormData) => {
    setIsLoading(true);
    setError('');

    try {
      const adData = {
        title: data.title,
        description: data.description,
        category: data.category,
        region: data.region,
        district: data.district,
        storeName: data.storeName,
        phone: data.phone,
        address: data.address,
        priceRange: data.priceRange,
        imageUrls: data.imageUrls,
        tags: data.tags,
      };

      if (isEditing && adId) {
        // Update existing ad
        const result = await updateAd(adId, adData);
        if (result) {
          onSuccess?.(adId);
          onClose();
        } else {
          setError('更新廣告失敗');
        }
      } else {
        // Create new ad
        const result = await createAd(adData);
        if (result) {
          // Navigate to member center or refresh list
          onSuccess?.(result.id || '');
          onClose();
        } else {
          setError('建立廣告失敗');
        }
      }
    } catch (err) {
      console.error('Failed to save ad:', err);
      setError(isEditing ? '更新廣告失敗' : '建立廣告失敗');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (isLoadingAd) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="large">
        <div className="ad-form-modal__loading">載入中...</div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      bodyClassName="modal__body--ad-form"
      title={isEditing ? '編輯廣告' : '發布廣告'}
      desktopOnly={true}
    >
      <AdForm
        initialData={initialData ? {
          title: initialData.title,
          description: initialData.description || undefined,
          category: initialData.category,
          region: initialData.region,
          district: initialData.district || undefined,
          storeName: initialData.storeName,
          phone: initialData.phone || undefined,
          address: initialData.address || undefined,
          priceRange: initialData.priceRange || undefined,
          imageUrls: initialData.imageUrls,
          tags: initialData.tags,
        } : undefined}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isEditing={isEditing}
        isLoading={isLoading}
        error={error}
      />
    </Modal>
  );
};

export default AdFormModal;
